import { useEffect, useRef } from "react";

/**
 * useHistoryModal(open, onClose)
 *
 * Pushes a synthetic history entry while an in-app modal/drawer is open so
 * the hardware/browser Back button closes the modal instead of navigating
 * away from the current page.
 *
 * Design (v4 — self-consuming listener):
 *   - The popstate handler REMOVES ITSELF when triggered, whether by the
 *     user pressing Back or by our own cleanup `history.back()`.
 *   - Cleanup sets a local `suppressNext` flag then calls `history.back()`
 *     but deliberately does NOT `removeEventListener` while `pushed` was
 *     true — the async popstate that follows will land on the still-
 *     attached handler, which self-removes after decrementing the flag.
 *     This keeps the suppress token strictly LOCAL to this hook instance
 *     and cannot poison a later modal's Back handling.
 *   - `pushState(currentState, '')` with NO URL change so React Router 6's
 *     own history stack is not disturbed.
 */
export const useHistoryModal = (open, onClose) => {
  const pushed = useRef(false);
  const suppressNext = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    if (typeof window === "undefined") return undefined;

    window.history.pushState(window.history.state, "");
    pushed.current = true;

    const handler = () => {
      if (suppressNext.current) {
        suppressNext.current = false;
        window.removeEventListener("popstate", handler);
        return;
      }
      if (pushed.current) {
        pushed.current = false;
        window.removeEventListener("popstate", handler);
        try {
          onCloseRef.current && onCloseRef.current();
        } catch (_) {
          /* noop */
        }
      }
    };
    window.addEventListener("popstate", handler);

    return () => {
      if (pushed.current) {
        // Modal was closed programmatically (X, overlay, state change).
        // Mark suppress so our own history.back() popstate is swallowed
        // by the still-attached handler, which then self-removes.
        pushed.current = false;
        suppressNext.current = true;
        try {
          window.history.back();
        } catch (_) {
          suppressNext.current = false;
          window.removeEventListener("popstate", handler);
        }
        // Do NOT removeEventListener here — the handler will self-remove
        // when the async popstate arrives. Removing early would leave
        // suppressNext stuck at true and break the next modal.
      } else {
        // Modal was closed via user Back — handler already self-removed.
        window.removeEventListener("popstate", handler);
      }
    };
  }, [open]);
};

export default useHistoryModal;
